import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Plus, Pencil } from "lucide-react"
import { toast } from "sonner"
import { api, type ApiEnvelope, type CompanyValue } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { AppIcon } from "@/lib/icon"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FieldError } from "@/components/ui/field-error"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { ConfirmDelete } from "@/components/admin/confirm-delete"

export const Route = createFileRoute("/admin/_authed/company-values")({
  component: CompanyValuesPage,
})

type FormState = {
  title: string
  description: string
  icon: string
  order: number
  is_active: boolean
}
const empty: FormState = { title: "", description: "", icon: "", order: 0, is_active: true }

function CompanyValuesPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<CompanyValue[]>({
    queryKey: ["admin", "company-values"],
    queryFn: async () => (await api.get<ApiEnvelope<CompanyValue[]>>("/admin/company-values")).data.data,
  })

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<CompanyValue | null>(null)
  const [form, setForm] = useState<FormState>(empty)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "company-values"] })
    queryClient.invalidateQueries({ queryKey: ["company-values"] })
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title,
        description: form.description,
        icon: form.icon,
        order: form.order,
        is_active: form.is_active,
      }
      if (editing) {
        await api.put(`/admin/company-values/${editing.id}`, payload)
      } else {
        await api.post("/admin/company-values", payload)
      }
    },
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setFieldErrors({})
      toast.success(editing ? "Value updated." : "Value created.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/admin/company-values/${id}`),
    onSuccess: () => {
      invalidate()
      toast.success("Value deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(empty)
    setFieldErrors({})
    setOpen(true)
  }
  const openEdit = (item: CompanyValue) => {
    setEditing(item)
    setForm({
      title: item.title,
      description: item.description ?? "",
      icon: item.icon ?? "",
      order: item.order,
      is_active: item.is_active,
    })
    setFieldErrors({})
    setOpen(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Company Values</h1>
          <p className="mt-1 text-sm text-muted-foreground">The "What drives us" cards on the about page.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full">
              <Plus className="h-4 w-4" /> New value
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit value" : "New value"}</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    aria-invalid={!!fieldErrors.title}
                  />
                  <FieldError message={fieldErrors.title} />
                </div>
                <div>
                  <Label htmlFor="icon">Icon</Label>
                  <Input
                    id="icon"
                    placeholder="e.g. rocket, shield-check"
                    value={form.icon}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">Kebab-case lucide.dev icon name.</p>
                </div>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
                <div>
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  />
                </div>
                <div className="flex items-center gap-3 pb-2">
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} id="is_active" />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="text-sm text-destructive">Couldn't load company values.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(data ?? []).map((v) => {
              return (
                <div key={v.id} className="rounded-2xl border border-border bg-card p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink text-cream">
                    <AppIcon name={v.icon} className="h-5 w-5" />
                  </div>
                  <p className="mt-3 font-semibold">{v.title}</p>
                  {v.description && <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{v.description}</p>}
                  <Badge className="mt-3" variant={v.is_active ? "default" : "secondary"}>
                    {v.is_active ? "Active" : "Hidden"}
                  </Badge>
                  <div className="mt-4 flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(v)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <ConfirmDelete itemLabel={v.title} onConfirm={() => remove.mutate(v.id)} />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
